/**
 * FilterBar Component
 *
 * A responsive, grid-based filter bar that scales from 1-4 columns automatically.
 * Supports adding new filters easily via configuration objects.
 *
 * Usage:
 * ```tsx
 * const filters: FilterConfig[] = [
 *   {
 *     id: 'status',
 *     type: 'select',
 *     label: 'Status',
 *     value: statusFilter,
 *     onChange: setStatusFilter,
 *     options: [{ value: '', label: 'All' }, ...],
 *   },
 *   {
 *     id: 'search',
 *     type: 'text',
 *     label: 'Search',
 *     value: searchFilter,
 *     onChange: setSearchFilter,
 *     placeholder: 'Search...',
 *   },
 * ];
 *
 * <FilterBar
 *   filters={filters}
 *   actions={<button>Export</button>}
 * />
 * ```
 */

import { type CSSProperties } from 'react';
import { FilterInput } from './FilterInput';
import type { FilterBarProps } from './types';

export function FilterBar({ filters, columns = 'auto', gap = '16px', actions }: FilterBarProps) {
  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const filtersGridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns:
      columns === 'auto' ? 'repeat(auto-fit, minmax(220px, 1fr))' : `repeat(${columns}, 1fr)`,
    gap,
    alignItems: 'end',
  };

  const actionsContainerStyle: CSSProperties = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  return (
    <div style={containerStyle}>
      {/* Filter Grid */}
      <div style={filtersGridStyle}>
        {filters.map((filter) => (
          <FilterInput key={filter.id} config={filter} />
        ))}
      </div>

      {/* Action Buttons (if provided) */}
      {actions && <div style={actionsContainerStyle}>{actions}</div>}
    </div>
  );
}
